import z from "zod";

import type { IntakeFormPayload } from "@/features/billing/api";
import type { OnboardingUser } from "@/features/onboarding/models";
import { Category, SUBSCRIPTION_VENDOR } from "@/shared/domains";
import type { Coupon } from "@/shared/domains";
import { Authorize } from "@/shared/domains/auth/models";
import { parseResponseData, publicAPI } from "@/shared/lib";
import { getAttributionCookie } from "@/shared/lib/attribution-cookie";
import type { PasswordFormValues } from "@/shared/models/legacy-types";
import { formatPhoneNumber } from "@/shared/utils";
import { onboardingUserSchema } from "../models";
import type { CreateUser } from "../models";

export const validateEmail = async (email: OnboardingUser["email"]) => {
  return await publicAPI.get("/users/email/check-availability", {
    params: { email },
  });
};

export const createUser = async ({
  weightInPounds,
  eligibilityAnswerIds,
  ...params
}: CreateUser) => {
  const attribution = getAttributionCookie();
  const efTransactionId = attribution?._ef_transaction_id;
  const affid = attribution?.affid;
  const oid = attribution?.oid;
  const sourceId = attribution?.source_id;
  const uid = attribution?.uid;
  const sub1 = attribution?.sub1;
  const sub2 = attribution?.sub2;
  const sub3 = attribution?.sub3;
  const sub4 = attribution?.sub4;
  const sub5 = attribution?.sub5;

  const response = await publicAPI.post("/users", {
    weight: weightInPounds,
    onboardingAnswers: eligibilityAnswerIds,
    ...params,
    ...(efTransactionId && { efTransactionId }),
    ...(affid && { affid }),
    ...(oid && { oid }),
    ...(sourceId && { sourceId }),
    ...(uid && { uid }),
    ...(sub1 && { sub1 }),
    ...(sub2 && { sub2 }),
    ...(sub3 && { sub3 }),
    ...(sub4 && { sub4 }),
    ...(sub5 && { sub5 }),
  });

  return parseResponseData(
    response,
    onboardingUserSchema.omit({
      shipping: true,
      billing: true,
      phoneNumber: true,
      state: true,
    }),
  );
};

interface EverflowParams {
  efTransactionId: string;
  affid: string;
  oid: string;
  prices: number[];
}

type CompleteOnboardingParams = Pick<
  OnboardingUser,
  "code" | "state" | "shipping" | "phoneNumber"
> & {
  discount?: Coupon["code"];
  everflowParams?: EverflowParams;
  isCallCenter: boolean;
  intakeForm?: IntakeFormPayload;
};

const completeOnboardingResponseSchema = z.object({
  token: onboardingUserSchema.shape.token.nonoptional(),
  intakeResult: z
    .object({
      needsSync: z.boolean(),
      vendor: z.enum(SUBSCRIPTION_VENDOR).nullable(),
    })
    .nullable()
    .optional(),
});

export const completeOnboarding = async ({
  code,
  shipping,
  everflowParams,
  ...params
}: CompleteOnboardingParams) => {
  const response = await publicAPI.post(
    `/patients/${code}/onboarding/complete`,
    {
      ...params,
      city: shipping.city,
      line1: shipping.address,
      line2: shipping.apartmentNumber,
      zip: shipping.zipCode,
      ...(everflowParams && {
        everflow: {
          _ef_transaction_id: everflowParams.efTransactionId,
          affid: everflowParams.affid,
          oid: everflowParams.oid,
          prices: everflowParams.prices,
        },
      }),
    },
  );

  return parseResponseData(response, completeOnboardingResponseSchema);
};

export const sendCheckoutLink = async (code: OnboardingUser["code"]) => {
  await publicAPI.post(`/patients/${code}/onboarding/send-checkout-link`);
};

type SetPasswordParams = PasswordFormValues & { token: string };

export const setPassword = async (params: SetPasswordParams) => {
  const response = await publicAPI.put("/patients/set-password", params);

  return parseResponseData(response, Authorize);
};

export const getOnboardingUser = async (code: OnboardingUser["code"]) => {
  const response = await publicAPI.get(`users/${code}/onboarding-contact`);

  return parseResponseData(
    response,
    onboardingUserSchema
      .pick({
        code: true,
        firstName: true,
        email: true,
        phoneNumber: true,
      })
      .extend({
        selectedCategories: Category.omit({ treatments: true }).array(),
        phoneNumber: z
          .string()
          .transform((value) => formatPhoneNumber(value) ?? ""),
      }),
  );
};
